import React, { useEffect, memo, useMemo, useState, useCallback } from 'react';
import './index.less';
import { Checkbox } from 'antd';
import classNames from 'classnames';
import DetailModal from '@/pages/prediction-score/components/detail-modal';
import ExamSyllabus from '@/pages/prediction-score/components/exam-syllabus';
import CompletedAnimation from '@/pages/prediction-score/components/completed-animation';
import GuideComponent from '@/pages/prediction-score/components/guide';
export { default as AppProvider } from '@/pages/prediction-score/context/AppProvider';
import { useAuthContext } from '@/pages/prediction-score/context/AuthContext';
import { useCommonInfoContext } from '@/pages/prediction-score/context/CommonInfoProvider';
import { IHomePagePredictionScore, IHomePageExamSyllabus, IPredictScoreCompletedExamPoint } from '@/types';
import { getHomePagePredictionScore, getPredictScoreCompletedExamPoint } from '@/apis';
import { PredictionScoreCardIcon, PredictionScoreReportCardIcon } from '@/svgs';
import globalStore from '@/store/global';
import { bindEvents } from '@/utils/sensors';
import { useAppStore } from '@/store';
import { environment } from '@gaodun.com/gtools';

const userReportOptions = [
    { label: '一', value: '0' },
    { label: '二', value: '1' },
    { label: '三', value: '2' },
    { label: '四', value: '3' },
    { label: '五', value: '4' },
    { label: '六', value: '5' },
    { label: '日', value: '6' },
];

const Index = () => {
    const [initInfo, setInitInfo] = useState<{
        projectId: number;
        subjectId: number;
        examSeasonId: number;
        isLoggedIn: boolean;
        sourceFromType: number;
        businessParam?: { [key: string]: number }; // 课程场景下可能会传
    }>();
    const [examSyllabus, setExamSyllabus] = useState<IHomePageExamSyllabus>({
        contents: [],
        examSyllabusId: 0,
        title: '',
        projectId: 0,
        subjectId: 0,
        knowledgeCount: 0,
    });
    const [predictionScore, setPredictionScore] = useState<IHomePagePredictionScore['predictionScore']>({
        goalScore: 0,
        predictionScore: 0,
        examSeasonId: 0,
        examSyllabusId: 0,
    });
    const [userReportCalendar, setUserReportCalendar] = useState<{
        userReportUrl: string;
        userReportDay: string[];
    }>();
    const [completedExamPoint, setCompletedExamPoint] = useState<{
        count: number;
        list: IPredictScoreCompletedExamPoint['knowledgeList'][];
    }>();
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    const isGoalCompleted =
        predictionScore?.goalScore && predictionScore?.predictionScore
            ? predictionScore.predictionScore >= predictionScore.goalScore
            : false;

    const isShowGuide = initInfo?.isLoggedIn && !!examSyllabus.examSyllabusId;

    const { authStatus, dispatch, withAuthCheck } = useAuthContext();
    const { dispatch: commonInfoDispatch } = useCommonInfoContext();
    const {
        themeStore: { theme },
    } = useAppStore();

    const fetchData = useCallback(async () => {
        const { projectId, subjectId, examSeasonId, isLoggedIn } = initInfo!;
        const response = await getHomePagePredictionScore(
            {
                projectId,
                subjectId,
                examSeasonId,
            },
            { isVisit: !initInfo!.isLoggedIn }
        );
        if (response.status === 0 && response.result) {
            const { examSyllabus, hasAuth, noAuthTip, predictionScore, userReportCalendar } = response.result;

            commonInfoDispatch({
                type: 'UPDATE_COMMON_INFO',
                ...initInfo!,
                examSyllabusId: examSyllabus.examSyllabusId,
            });

            dispatch({
                type: 'SET_AUTH_STATUS',
                isLoggedIn: isLoggedIn,
                hasAuth: hasAuth === 1,
                noAuthTip: noAuthTip,
            });

            setExamSyllabus(examSyllabus);
            setPredictionScore(predictionScore);
            setUserReportCalendar({
                userReportUrl: userReportCalendar?.userReportUrl ?? '',
                userReportDay:
                    userReportCalendar?.practiceStatusOfWeek
                        .map((status, index) => (status === 1 ? `${index}` : undefined))
                        .filter(day => day !== undefined) ?? [],
            });
        }
    }, [initInfo, dispatch, commonInfoDispatch]);

    const fetchCompletedExamPoint = useCallback(async () => {
        const { projectId, subjectId, examSeasonId, isLoggedIn } = initInfo!;

        if (!isLoggedIn) return;

        const response = await getPredictScoreCompletedExamPoint({
            projectId,
            subjectId,
            examSeasonId,
            devMode: environment.getApiEnv() === 't-' && localStorage.getItem('devMode') ? 1 : 0,
        });

        if (response.status === 0 && response.result?.count) {
            const { knowledgeList, count } = response.result;
            const list = [];
            for (let i = 0; i < knowledgeList.length; i += 3) {
                list.push(knowledgeList.slice(i, i + 3));
            }
            setCompletedExamPoint({ count, list });
        }
    }, [initInfo]);

    const handleOpenDetailModal = withAuthCheck(
        () => {
            setIsDetailModalOpen(true);
        },
        () =>
            bindEvents('Click_PredictedScore', {
                name: '点击预测分卡片',
                project_id: initInfo!.projectId,
                subject_id: initInfo!.subjectId,
            })
    );

    const handleGotoUserReport = withAuthCheck(
        () => {
            if (userReportCalendar?.userReportUrl) {
                const url = new URL(userReportCalendar.userReportUrl);
                const params = new URLSearchParams(url.search);

                // 添加 isPc 参数
                params.set('isPc', '1');

                // 根据路由条件添加 from 参数
                if (location.href.includes('ep3')) {
                    params.set('from', '6');
                } else if (location.href.includes('tiku.gaodun.com')) {
                    params.set('from', '4');
                }

                // 更新 URL 的 search 部分
                url.search = params.toString();

                // 打开新窗口
                window.open(url.toString(), '_blank');
            }
        },
        () =>
            bindEvents('Click_UserReport', {
                name: '点击用户报告',
                project_id: initInfo!.projectId,
                subject_id: initInfo!.subjectId,
            })
    );

    useEffect(() => {
        const initialize = async () => {
            if (initInfo?.examSeasonId) {
                await fetchData();
                await fetchCompletedExamPoint();
            }
        };

        initialize();
    }, [initInfo?.examSeasonId]);

    useEffect(() => {
        globalStore.subscribe('PredictionScore', ({ type, params }) => {
            switch (type) {
                case 'init':
                    setInitInfo({
                        ...params,
                        isLoggedIn: params.isLogin ?? true, // 课程内默认登录状态
                        sourceFromType: params.sourceFromType ?? 100534177, // 100534177 新题库id
                    });

                    dispatch({
                        type: 'SET_AUTH_STATUS',
                        ...authStatus,
                        isLoggedIn: params.isLogin ?? true, // 课程内默认登录状态
                    });
                    break;
                case 'open_predict_score_feedback':
                    globalStore.publish('PredictScoreFeedback', { type: 'open', params });
                    break;
                default:
                    setInitInfo(undefined);
                    break;
            }
        });

        return () => {
            globalStore.unsubscribe('PredictionScore');
        };
    }, []);

    return (
        <>
            <div className="sub-tiku__prediction-score-card-wrap" id="predict-score-guide">
                <div
                    className={classNames('sub-tiku__prediction-score-card', {
                        completed: isGoalCompleted,
                    })}
                    onClick={handleOpenDetailModal}
                >
                    <div className="sub-tiku__prediction-score-card-title">
                        <span className="em">预测分</span>/目标分
                    </div>
                    <div className="sub-tiku__prediction-score-card-content">
                        <div className="prediction-score-numbers">
                            <span className="strong">{predictionScore.predictionScore ?? '--'}</span>/{predictionScore.goalScore}
                        </div>
                    </div>
                    <PredictionScoreCardIcon color={isGoalCompleted ? theme.successColor : theme.mainColor} />
                </div>
                <div className="sub-tiku__prediction-score-card-placeholder" />
                <div
                    className="sub-tiku__prediction-score-card sub-tiku__prediction-score-card--user-report"
                    onClick={handleGotoUserReport}
                >
                    <div className="sub-tiku__prediction-score-card-title">用户报告</div>
                    <div className="sub-tiku__prediction-score-card-content">
                        <Checkbox.Group options={userReportOptions} value={userReportCalendar?.userReportDay} onChange={undefined} />
                    </div>
                    <PredictionScoreReportCardIcon />
                </div>
            </div>
            <DetailModal open={isDetailModalOpen} setOpen={setIsDetailModalOpen} onUpdate={fetchData} />
            <ExamSyllabus examSyllabus={examSyllabus} />
            {completedExamPoint?.count && (
                <CompletedAnimation dataSource={completedExamPoint} onClose={() => setCompletedExamPoint(undefined)} />
            )}
            {isShowGuide && <GuideComponent />}
        </>
    );
};

export default memo(Index);
